import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { useTheme } from "../context/ThemeContext";

export default function PrivacyPolicy() {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#050505]' : 'bg-slate-100'} transition-colors duration-300`}>
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/">
            <Button variant="ghost" size="sm" className="rounded-xl">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>

        <div className="glass rounded-2xl p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <Shield className={`w-8 h-8 ${isDarkMode ? 'text-[#00f2ea]' : 'text-cyan-600'}`} />
            <h1 className="text-3xl font-bold">Privacy Policy</h1>
          </div>
          
          <p className="opacity-60 mb-8">Last updated: December 21, 2025</p>

          <div className="space-y-8 opacity-90">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
              <p className="leading-relaxed">
                Welcome to TheForecastTracker ("we," "our," or "us"). We are committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our weather application and services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Information We Collect</h2>
              <h3 className="font-medium mb-2">2.1 Information You Provide</h3>
              <ul className="list-disc list-inside space-y-1 mb-4">
                <li>Account information (name, email address, password)</li>
                <li>Location preferences you save</li>
                <li>Temperature unit preferences</li>
                <li>Communication preferences</li>
              </ul>
              
              <h3 className="font-medium mb-2">2.2 Automatically Collected Information</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Device location (with your permission) for local weather data</li>
                <li>IP address for approximate location when GPS is unavailable</li>
                <li>Device type, browser type, and operating system</li>
                <li>Usage data and interaction with our services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. How We Use Your Information</h2>
              <p className="mb-2">We use the collected information to:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Provide accurate, location-based weather forecasts</li>
                <li>Personalize your experience and save your preferences</li>
                <li>Send weather alerts and notifications (if enabled)</li>
                <li>Improve our services and develop new features</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Ensure the security and integrity of our services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Information Sharing</h2>
              <p className="mb-2">We do not sell your personal information. We may share information with:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Weather Data Providers:</strong> To fetch weather information for your location</li>
                <li><strong>Service Providers:</strong> Third parties that help us operate our services</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Data Security</h2>
              <p className="leading-relaxed">
                We implement industry-standard security measures to protect your information, including encryption, secure servers, and regular security audits. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Your Rights and Choices</h2>
              <p className="mb-2">You have the right to:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Access, update, or delete your personal information</li>
                <li>Opt-out of marketing communications</li>
                <li>Disable location services (may limit functionality)</li>
                <li>Request a copy of your data</li>
                <li>Delete your account at any time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Cookies and Tracking</h2>
              <p className="leading-relaxed">
                We use cookies and similar technologies to enhance your experience, remember your preferences, and analyze usage patterns. You can control cookies through your browser settings. See our Cookie Policy for more details.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Children's Privacy</h2>
              <p className="leading-relaxed">
                Our services are not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Changes to This Policy</h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. Contact Us</h2>
              <p className="leading-relaxed">
                If you have questions about this Privacy Policy or our practices, please contact us at:
              </p>
              <div className="mt-3 p-4 rounded-xl bg-white/5">
                <p><strong>TheForecastTracker</strong></p>
                <p>Email: privacy@theforecasttracker.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
